/**
 * Fleet Health Dashboard — a top-level admin tab. One probe over every saved
 * connection (server-side, bounded-parallel): version, uptime, size, thread
 * count, engine + charset audits (the read-only halves of the planned engine /
 * charset tools), and last-backup time. Live data only — nothing persisted;
 * an unreachable connection is an honest offline row.
 */
import { createSignal, createResource, createMemo, createEffect, onCleanup, For, Show } from 'solid-js';
import { formatBytes, relTime } from '../lib/format.js';
import { probeFleetHealth } from '../lib/fleethealth.js';

const AUTO_REFRESH_MS = 30000;
const OFFENDER_CAP = 30; // drill-down list display cap

function fmtUptime(sec) {
  if (!sec) return '—';
  const d = Math.floor(sec / 86400), h = Math.floor((sec % 86400) / 3600), m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// {InnoDB:84, MyISAM:12} → "84 InnoDB · 12 MyISAM" (count desc, then name).
function breakdownText(counts) {
  const entries = Object.entries(counts || {});
  if (!entries.length) return '—';
  entries.sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1));
  return entries.map(([k, n]) => `${n} ${k}`).join(' · ');
}

export default function FleetHealthPanel() {
  const [data, { refetch }] = createResource(probeFleetHealth);
  const [auto, setAuto] = createSignal(false);
  const [group, setGroup] = createSignal(''); // '' = all
  const [expandedId, setExpandedId] = createSignal(0);

  createEffect(() => {
    if (!auto()) return;
    const t = setInterval(() => { if (!data.loading) refetch(); }, AUTO_REFRESH_MS);
    onCleanup(() => clearInterval(t));
  });

  const conns = () => data()?.connections || [];
  const groups = createMemo(() => {
    const set = new Set(conns().map((c) => c.groupName || 'Ungrouped'));
    return [...set].sort((a, b) => (a === 'Ungrouped' ? 1 : b === 'Ungrouped' ? -1 : a < b ? -1 : 1));
  });
  const visible = createMemo(() =>
    group() ? conns().filter((c) => (c.groupName || 'Ungrouped') === group()) : conns());

  const probedAt = () => {
    const t = data()?.probedAt;
    return t ? new Date(t * 1000).toLocaleTimeString() : '';
  };

  const toggleExpand = (c) => {
    if (!c.reachable || !c.summary) return;
    setExpandedId(expandedId() === c.connId ? 0 : c.connId);
  };

  const mixedCharsets = (s) => Object.keys(s?.charsetCounts || {}).length > 1;
  const myisamCount = (s) => s?.engineCounts?.MyISAM || 0;

  return (
    <div class="fhealth-panel">
      <div class="fhealth-head">
        <div>
          <h2>Fleet Health</h2>
          <p class="muted">Live probe over every connection — version, size, engine &amp; charset audits, last backup.</p>
        </div>
        <div class="fhealth-actions">
          <Show when={probedAt()}><span class="muted fhealth-stamp">probed {probedAt()}</span></Show>
          <label class="fhealth-auto">
            <input type="checkbox" checked={auto()} onInput={(e) => setAuto(e.currentTarget.checked)} />
            Auto-refresh
          </label>
          <button class="btn" disabled={data.loading} onClick={refetch}>
            {data.loading ? 'Probing…' : '⟳ Refresh'}
          </button>
        </div>
      </div>

      <Show when={groups().length > 1}>
        <div class="fhealth-chips">
          <button class={`day-chip ${group() === '' ? 'on' : ''}`} onClick={() => setGroup('')}>All</button>
          <For each={groups()}>
            {(g) => (
              <button class={`day-chip ${group() === g ? 'on' : ''}`} onClick={() => setGroup(g)}>{g}</button>
            )}
          </For>
        </div>
      </Show>

      <Show when={!data.loading || data()} fallback={<div class="muted">Probing the fleet…</div>}>
        <table class="jobs-table fhealth-table">
          <thead>
            <tr>
              <th></th>
              <th>Connection</th>
              <th>Server</th>
              <th>Uptime</th>
              <th>Size</th>
              <th>Conns</th>
              <th>Engines</th>
              <th>Charsets</th>
              <th>Last backup</th>
            </tr>
          </thead>
          <tbody>
            <For each={visible()}>
              {(c) => (
                <>
                  <tr
                    class={`fhealth-row ${c.reachable && c.summary ? 'fhealth-expandable' : ''} ${expandedId() === c.connId ? 'fhealth-open' : ''}`}
                    onClick={() => toggleExpand(c)}
                  >
                    <td><span class={`fhealth-dot ${c.reachable ? 'fhealth-dot-ok' : 'fhealth-dot-bad'}`} /></td>
                    <td>
                      <span class="fhealth-name">
                        <Show when={c.color}><span class="conn-swatch" style={`background:${c.color}`} /></Show>
                        {c.name}
                      </span>
                      <Show when={c.groupName}><span class="muted fhealth-group"> · {c.groupName}</span></Show>
                    </td>
                    <Show
                      when={c.reachable}
                      fallback={<td colSpan={7} class="fhealth-offline-msg">offline — {c.error || 'unreachable'}</td>}
                    >
                      <td>{c.server?.display || '—'}</td>
                      <td>{fmtUptime(c.uptimeSec)}</td>
                      <td>{c.summary ? formatBytes(c.summary.totalSize) : '—'}</td>
                      <td>{c.threadsConnected}</td>
                      <td>
                        {breakdownText(c.summary?.engineCounts)}
                        <Show when={myisamCount(c.summary) > 0}>
                          <span class="fhealth-badge-warn" title="MyISAM tables — no transactions/crash recovery">⚠</span>
                        </Show>
                      </td>
                      <td>
                        {breakdownText(c.summary?.charsetCounts)}
                        <Show when={mixedCharsets(c.summary)}>
                          <span class="fhealth-badge-warn" title="Mixed character sets across tables">⚠</span>
                        </Show>
                      </td>
                      <td>
                        <Show when={c.lastBackup} fallback={<span class="fhealth-never">never</span>}>
                          {relTime(c.lastBackup.at)} <span class="muted">· {c.lastBackup.source}</span>
                        </Show>
                      </td>
                    </Show>
                  </tr>
                  <Show when={c.reachable && c.error}>
                    <tr class="fhealth-row"><td></td><td colSpan={8} class="fhealth-offline-msg">stats unavailable — {c.error}</td></tr>
                  </Show>
                  <Show when={expandedId() === c.connId && c.summary}>
                    <tr class="fhealth-drill-row">
                      <td></td>
                      <td colSpan={8}>
                        <Drill summary={c.summary} />
                      </td>
                    </tr>
                  </Show>
                </>
              )}
            </For>
          </tbody>
        </table>
        <Show when={!visible().length}><div class="muted">No connections.</div></Show>
      </Show>
    </div>
  );
}

function Drill(props) {
  const s = () => props.summary;
  const maxDb = createMemo(() => Math.max(1, ...s().dbSizes.map((d) => d.size)));
  const outliers = () => s().charsetOutliers;
  const myisam = () => s().myisamTables;
  return (
    <div class="fhealth-drill">
      <div class="fhealth-section">
        <div class="fhealth-section-title">Databases ({s().dbSizes.length})</div>
        <For each={s().dbSizes}>
          {(d) => (
            <div class="fhealth-dbrow">
              <span class="fhealth-dbname">{d.name}</span>
              <span class="fhealth-dbbar"><span class="fhealth-dbbar-fill" style={`width:${Math.max(2, Math.round((d.size / maxDb()) * 100))}%`} /></span>
              <span class="fhealth-dbsize">{formatBytes(d.size)} · {d.tables} tables</span>
            </div>
          )}
        </For>
      </div>
      <div class="fhealth-section">
        <div class="fhealth-section-title">Biggest tables</div>
        <For each={s().topTables}>
          {(t) => (
            <div class="fhealth-tabrow">
              <span class="fhealth-tabname">{t.schema}.{t.name}</span>
              <span class="muted">{t.engine} · ~{t.rows.toLocaleString()} rows</span>
              <span class="fhealth-dbsize">{formatBytes(t.size)}</span>
            </div>
          )}
        </For>
      </div>
      <Show when={myisam().length || outliers().length}>
        <div class="fhealth-section">
          <div class="fhealth-section-title">Audit findings</div>
          <Show when={myisam().length}>
            <div class="fhealth-finding">⚠ {myisam().length} MyISAM table{myisam().length === 1 ? '' : 's'}</div>
            <OffenderList items={myisam()} label={(t) => `${t.schema}.${t.name} (${formatBytes(t.size)})`} />
          </Show>
          <Show when={outliers().length}>
            <div class="fhealth-finding">⚠ {outliers().length} table{outliers().length === 1 ? '' : 's'} not {s().majorityCharset}</div>
            <OffenderList items={outliers()} label={(t) => `${t.schema}.${t.name} (${t.charset})`} />
          </Show>
        </div>
      </Show>
    </div>
  );
}

function OffenderList(props) {
  const shown = () => props.items.slice(0, OFFENDER_CAP);
  const more = () => props.items.length - shown().length;
  return (
    <ul class="fhealth-offenders">
      <For each={shown()}>{(t) => <li>{props.label(t)}</li>}</For>
      <Show when={more() > 0}><li class="muted">…and {more()} more</li></Show>
    </ul>
  );
}
